/**
 * ETL pipeline designer (ADR-0038, P4) — the canvas editor for one pipeline.
 *
 * Mirrors the workflow editor: a ReactFlow canvas (descriptor-driven custom
 * nodes + dynamic handles), a left block palette (drag/drop or click-to-add), a
 * right descriptor-driven config panel, and a toolbar (save version · run ·
 * validate). Live per-node metrics arrive over the global SSE stream
 * (`etl_run_*` events) and overlay each node's status ring during a run.
 *
 * Look&feel is verified visually by the owner (no browser-drive). Pure graph
 * logic (validation / ports / converters) is unit-tested in `etlGraph.test.ts`.
 */
import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactFlow, {
  addEdge,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeTypes,
  type Node,
  type NodeTypes,
} from "reactflow";
import "reactflow/dist/style.css";
import { AlertTriangle, ArrowLeft, Bug, CheckCircle2, History, Loader2, Play, Save, Settings2, Sparkles, Square, X } from "lucide-react";

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useFilteredEvents } from "@/lib/eventStream";
import { usePermissions } from "@/lib/permissions";
import { uid } from "@/lib/uid";
import {
  etlApi,
  type BlockDescriptor,
  type EtlRunEventLike,
  type NodeMetricSnapshot,
  type OverlapPolicy,
  type RunDetail,
  type RunStatus,
} from "@/lib/api-etl";
import { cn } from "@/lib/cn";

import { BlockConfigPanel } from "./BlockConfigPanel";
import { BlockPalette } from "./BlockPalette";
import { CronEditor } from "./CronEditor";
import { EtlBlockNode } from "./EtlBlockNode";
import { EtlFlowEdge } from "./EtlFlowEdge";
import {
  type EtlNodeData,
  fromDefinition,
  getEffectiveInputPorts,
  getEffectiveOutputPorts,
  toDefinition,
  validateConnection,
  validatePipeline,
} from "./etlGraph";

const NODE_TYPES: NodeTypes = { etlBlock: EtlBlockNode };
const EDGE_TYPES: EdgeTypes = { etlFlow: EtlFlowEdge };

/** Pipeline settings dialog — TWO concerns:
 *  - Runtime knobs (Phase A#2): `definition.settings`, applied in-memory + persisted
 *    on the next Save (`onApply`).
 *  - Pianificazione (cron + tz) + corsia (lane FK): PIPELINE metadata, PATCHed
 *    IMMEDIATELY (`etlApi.update`) — these don't ride the version definition.
 *  A number field clears its key when blanked (→ engine default). */
function SettingsDialog({
  pipelineId,
  settings,
  scheduleCron,
  scheduleTimezone,
  scheduleOverlapPolicy,
  laneId,
  priority,
  onApply,
  onClose,
}: {
  pipelineId: string;
  settings: Record<string, unknown>;
  scheduleCron: string | null;
  scheduleTimezone: string | null;
  scheduleOverlapPolicy: OverlapPolicy;
  laneId: string | null;
  priority: number;
  onApply: (next: Record<string, unknown>) => void;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const num = (k: string) => {
    const v = settings[k];
    return typeof v === "number" ? String(v) : "";
  };
  const [mdop, setMdop] = React.useState(num("maxDegreeOfParallelism"));
  const [cap, setCap] = React.useState(num("boundedCapacity"));
  const [cron, setCron] = React.useState(scheduleCron ?? "");
  const [tz, setTz] = React.useState(scheduleTimezone ?? "");
  const [overlap, setOverlap] = React.useState<OverlapPolicy>(scheduleOverlapPolicy);
  const [lane, setLane] = React.useState(laneId ?? "");
  const [prio, setPrio] = React.useState(String(priority));

  const lanesQ = useQuery({ queryKey: ["etl", "lanes"], queryFn: () => etlApi.lanes.list() });

  const saveMut = useMutation({
    mutationFn: () => {
      const p = parseInt(prio, 10);
      return etlApi.update(pipelineId, {
        schedule_cron: cron.trim(),
        schedule_timezone: tz.trim(),
        schedule_overlap_policy: overlap,
        lane_id: lane || null,
        priority: Number.isNaN(p) ? 50 : Math.max(0, Math.min(100, p)),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["etl", pipelineId] });
      qc.invalidateQueries({ queryKey: ["etl", "list"] });
    },
  });

  const apply = () => {
    const next = { ...settings };
    const set = (k: string, raw: string, lo: number, hi: number) => {
      const n = parseInt(raw, 10);
      if (raw.trim() === "" || Number.isNaN(n)) delete next[k];
      else next[k] = Math.max(lo, Math.min(hi, n));
    };
    set("maxDegreeOfParallelism", mdop, 1, 64);
    set("boundedCapacity", cap, 1, 100000);
    onApply(next);
    // Schedule + lane persist immediately; close once they land.
    saveMut.mutate(undefined, { onSuccess: () => onClose() });
  };

  const inputCls =
    "h-9 w-full rounded-md border border-border bg-surface px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Impostazioni pipeline</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-3">
            <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Runtime</h3>
            <div className="space-y-1">
              <Label className="text-xs">Parallelismo per blocco (1–64)</Label>
              <Input
                type="number"
                value={mdop}
                onChange={(e) => setMdop(e.target.value)}
                placeholder="1 (serie)"
              />
              <p className="text-[11px] text-muted-foreground">
                K worker concorrenti sui blocchi parallelizzabili (transform/enricher
                stateless). Sorgenti, sink, buffer-all e dedup restano sempre a 1.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Capacità coda (backpressure)</Label>
              <Input
                type="number"
                value={cap}
                onChange={(e) => setCap(e.target.value)}
                placeholder="100 (default)"
              />
            </div>
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Pianificazione
            </h3>
            <CronEditor value={cron} onChange={setCron} timezone={tz || null} />
            <div className="space-y-1">
              <Label className="text-xs">Fuso orario (opzionale, IANA)</Label>
              <Input
                value={tz}
                onChange={(e) => setTz(e.target.value)}
                placeholder="Europe/Rome"
                spellCheck={false}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Se la run precedente è ancora in corso</Label>
              <select
                value={overlap}
                onChange={(e) => setOverlap(e.target.value as OverlapPolicy)}
                className={inputCls}
              >
                <option value="coalesce">Accavalla una sola (consigliato)</option>
                <option value="skip">Salta</option>
                <option value="allow">Accoda tutte</option>
              </select>
              <p className="text-[11px] text-muted-foreground">
                {overlap === "coalesce" &&
                  "Tiene al massimo una run in attesa: parte appena finisce quella in corso, senza accumulare."}
                {overlap === "skip" &&
                  "Salta l'orario se una run è già in corso o in coda: nessun accumulo, ma può restare un buco."}
                {overlap === "allow" &&
                  "Accoda ogni orario (con un tetto di sicurezza). Con una corsia + cron più veloce della durata la coda cresce."}
              </p>
            </div>
          </div>

          <div className="space-y-3 border-t border-border pt-4">
            <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Corsia &amp; priorità
            </h3>
            <div className="space-y-1">
              <Label className="text-xs">Corsia</Label>
              <select value={lane} onChange={(e) => setLane(e.target.value)} className={inputCls}>
                <option value="">Nessuna corsia</option>
                {(lanesQ.data ?? []).map((l) => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
              <p className="text-[11px] text-muted-foreground">
                Le pipeline nella stessa corsia non vengono eseguite contemporaneamente.
                Le corsie si gestiscono in <em>Corsie ETL</em>.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Priorità coda (0–100)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={prio}
                onChange={(e) => setPrio(e.target.value)}
                placeholder="50"
              />
              <p className="text-[11px] text-muted-foreground">
                A parità di coda, le run con priorità più alta partono prima (default 50).
              </p>
            </div>
          </div>
          {saveMut.isError && (
            <p className="text-[11px] text-danger">{(saveMut.error as Error).message}</p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={apply} disabled={saveMut.isPending}>
            {saveMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Applica
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function descriptorMap(catalog: Record<string, BlockDescriptor[]> | undefined): Map<string, BlockDescriptor> {
  const m = new Map<string, BlockDescriptor>();
  for (const list of Object.values(catalog ?? {})) for (const d of list) m.set(d.type, d);
  return m;
}

function EtlEditorInner({ pipelineId }: { pipelineId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = usePermissions();
  const canWrite = can("etl", "write");
  const canRun = can("etl", "execute");
  const rf = useReactFlow();

  const catalogQ = useQuery({ queryKey: ["etl", "blocks"], queryFn: () => etlApi.blocks(), staleTime: Infinity });
  const pipeQ = useQuery({ queryKey: ["etl", pipelineId], queryFn: () => etlApi.get(pipelineId) });

  const descriptors = React.useMemo(() => descriptorMap(catalogQ.data), [catalogQ.data]);

  const [nodes, setNodes, onNodesChange] = useNodesState<EtlNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [dirty, setDirty] = React.useState(false);
  const [activeRun, setActiveRun] = React.useState<RunDetail | null>(null);
  // Runtime knobs (maxDegreeOfParallelism / boundedCapacity), persisted in the
  // version definition — edited via the Impostazioni dialog (Phase A#2).
  const [settings, setSettings] = React.useState<Record<string, unknown>>({});
  const [showSettings, setShowSettings] = React.useState(false);

  // Hydrate the canvas once the pipeline + catalog are both loaded.
  const hydratedRef = React.useRef(false);
  React.useEffect(() => {
    if (hydratedRef.current || !pipeQ.data || descriptors.size === 0) return;
    hydratedRef.current = true;
    const def = pipeQ.data.definition;
    if (def) {
      const flow = fromDefinition(def, descriptors);
      setNodes(flow.nodes as Node<EtlNodeData>[]);
      setEdges(flow.edges as Edge[]);
      setSettings((def.settings as Record<string, unknown>) ?? {});
    }
  }, [pipeQ.data, descriptors, setNodes, setEdges]);

  // Draft-first (Odino): the enabled switch is the HUMAN gate for an
  // Odino-created pipeline (born disabled) — arming it is the operator's
  // explicit review sign-off. Plain metadata update, no version churn.
  const enableMut = useMutation({
    mutationFn: (enabled: boolean) => etlApi.update(pipelineId, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["etl", pipelineId] }),
  });

  // Draft-first (Odino): a modify writes an INACTIVE draft version — surface
  // it and let the operator activate it (their click is the confirmation).
  const draftVersion = React.useMemo(() => {
    const d = pipeQ.data;
    if (!d?.versions?.length) return null;
    const newest = d.versions.reduce((a, b) => (b.version > a.version ? b : a));
    if (d.active_version_id && newest.id === d.active_version_id) return null;
    const activeN = d.versions.find((v) => v.id === d.active_version_id)?.version ?? 0;
    return newest.version > activeN ? newest : null;
  }, [pipeQ.data]);
  const activateDraftMut = useMutation({
    mutationFn: (versionId: string) => etlApi.activate(pipelineId, versionId),
    onSuccess: () => {
      // Re-hydrate the canvas with the freshly activated definition.
      hydratedRef.current = false;
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["etl", pipelineId] });
    },
  });

  const selected = nodes.find((n) => n.id === selectedId) ?? null;

  // --- mutate a node's data (config / label) + recompute its ports ---------
  const patchNode = React.useCallback(
    (id: string, patch: Partial<EtlNodeData>) => {
      setDirty(true);
      setNodes((ns) =>
        ns.map((n) => {
          if (n.id !== id) return n;
          const data = { ...n.data, ...patch };
          if (patch.config) {
            data.inputs = getEffectiveInputPorts(data.descriptor, data.config);
            data.outputs = getEffectiveOutputPorts(data.descriptor, data.config);
          }
          return { ...n, data };
        }),
      );
    },
    [setNodes],
  );

  // --- add a node (drop or click) ------------------------------------------
  const addNode = React.useCallback(
    (descriptor: BlockDescriptor, position: { x: number; y: number }) => {
      if (!canWrite) return;
      const id = uid("etl");
      const data: EtlNodeData = {
        label: descriptor.ui.label,
        blockType: descriptor.type,
        descriptor,
        config: {},
        inputs: getEffectiveInputPorts(descriptor, {}),
        outputs: getEffectiveOutputPorts(descriptor, {}),
      };
      setNodes((ns) => [...ns, { id, type: "etlBlock", position, data }]);
      setDirty(true);
      setSelectedId(id);
    },
    [canWrite, setNodes],
  );

  const onDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const type = e.dataTransfer.getData("application/etl-block-type");
      const json = e.dataTransfer.getData("application/etl-descriptor");
      if (!type || !json) return;
      const descriptor = JSON.parse(json) as BlockDescriptor;
      const position = rf.project({ x: e.clientX, y: e.clientY });
      addNode(descriptor, position);
    },
    [rf, addNode],
  );

  const onConnect = React.useCallback(
    (params: Connection) => {
      const check = validateConnection(
        params.source!,
        params.target!,
        edges,
        nodes,
        params.sourceHandle,
        params.targetHandle,
      );
      if (!check.valid) return;
      setEdges((es) => addEdge({ ...params, type: "default" }, es));
      setDirty(true);
    },
    [edges, nodes, setEdges],
  );

  // --- save (new version) ---------------------------------------------------
  const saveMut = useMutation({
    mutationFn: () => {
      const positions = Object.fromEntries(nodes.map((n) => [n.id, n.position]));
      const def = toDefinition(
        { name: pipeQ.data!.name, description: pipeQ.data!.description ?? undefined },
        nodes.map((n) => ({ id: n.id, data: n.data })),
        edges,
        positions,
        settings,
      );
      return etlApi.createVersion(pipelineId, { definition: def, activate: true });
    },
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ["etl", pipelineId] });
    },
  });

  // --- run + LIVE metrics (driven entirely by the SSE stream) --------------
  // No polling: the engine publishes `etl_run_started` → per-node
  // `etl_run_metrics` (every ~0.5s) → `etl_run_finished` (final node_metrics +
  // status) on the always-on global stream. LIFECYCLE events (started/finished)
  // are processed ONCE by id (the dedup ref). METRICS are NOT id-deduped — they
  // share a stable id (collapsed for the Activity feed) and are idempotent, so the
  // effect re-applies the newest snapshot each tick (see the effect below).
  const processedRef = React.useRef<Set<string>>(new Set());

  const onRunStarted = React.useCallback(
    (run: RunDetail) => {
      processedRef.current = new Set();
      setActiveRun(run);
      setNodes((ns) =>
        ns.map((n) => ({ ...n, data: { ...n.data, runStatus: "idle", metrics: undefined } })),
      );
      qc.invalidateQueries({ queryKey: ["etl", pipelineId, "runs"] });
    },
    [pipelineId, qc, setNodes],
  );

  const runMut = useMutation({
    mutationFn: () => etlApi.run(pipelineId, false),
    onSuccess: onRunStarted,
  });

  // Debug run (ADR-0039 C4): caps each source to the first N rows + keeps a sample
  // that big, so a mapping is inspectable without streaming the whole origin. It's
  // synchronous server-side, so the returned run is already terminal — load its
  // node_metrics straight onto the canvas (no SSE wait).
  const debugMut = useMutation({
    mutationFn: () => etlApi.runDebug(pipelineId, 100),
    onSuccess: (run) => {
      onRunStarted(run);
      loadRun(run);
    },
  });

  // Stop a live run: flips `cancel_requested`; the engine's cancel-poller (on
  // whichever replica runs the job) tears the graph down and emits the terminal
  // `etl_run_finished` (status=cancelled), which the SSE effect below applies.
  const cancelMut = useMutation({
    mutationFn: () => etlApi.cancelRun(activeRun!.id),
    onSuccess: (run) => setActiveRun(run),
  });
  const runActive =
    activeRun?.status === "running" ||
    activeRun?.status === "pending" ||
    activeRun?.status === "cancelling";

  const runEvents = useFilteredEvents(
    (e) => (e as unknown as EtlRunEventLike).run_id === activeRun?.id && e.kind.startsWith("etl_run_"),
  );
  React.useEffect(() => {
    if (!activeRun) return;

    // LIVE METRICS — apply the NEWEST snapshot every pass (the buffer is
    // newest-first). The `etl_run_metrics` event carries a STABLE id
    // (`etl:metrics:<run_id>`, deliberately collapsed to ONE Activity-feed row),
    // so the per-id dedup loop below would FREEZE the live counters after the very
    // first tick — the bug where edges animate but the node numbers stay fixed. A
    // metrics snapshot is idempotent, so re-applying the latest is safe + correct;
    // `runStatus→running` is set here, the finished handler upgrades it to
    // done/failed (applyMetrics respects an already-terminal node).
    const latestMetrics = runEvents
      .map((e) => (e as unknown as EtlRunEventLike).node_metrics)
      .find((m): m is Record<string, NodeMetricSnapshot> => !!m);
    if (latestMetrics) applyMetrics(setNodes, latestMetrics);

    for (const raw of runEvents) {
      if (processedRef.current.has(raw.id)) continue;
      processedRef.current.add(raw.id);
      const ev = raw as unknown as EtlRunEventLike;

      if (ev.kind === "etl_run_started") {
        setActiveRun((r) => (r && r.status !== "running" ? { ...r, status: "running" } : r));
      }
      if (ev.kind === "etl_run_finished") {
        const st = (ev.status as RunStatus | undefined) ?? "succeeded";
        setActiveRun((r) =>
          r
            ? {
                ...r,
                status: st,
                rows_in: ev.rows_in ?? r.rows_in,
                rows_out: ev.rows_out ?? r.rows_out,
                dead_lettered: ev.dead_lettered ?? r.dead_lettered,
              }
            : r,
        );
        const failedRun = st !== "succeeded";
        const metrics = ev.node_metrics ?? {};
        setNodes((ns) =>
          ns.map((n) => {
            const m = metrics[n.id];
            const ok = m ? (m.errors ?? 0) === 0 : !failedRun;
            return { ...n, data: { ...n.data, metrics: m ?? n.data.metrics, runStatus: ok ? "done" : "failed" } };
          }),
        );
        qc.invalidateQueries({ queryKey: ["etl", pipelineId, "runs"] });
      }
    }
  }, [runEvents, activeRun, setNodes, qc, pipelineId]);

  // --- execution history ----------------------------------------------------
  const [showHistory, setShowHistory] = React.useState(false);
  const runsQ = useQuery({
    queryKey: ["etl", pipelineId, "runs"],
    queryFn: () => etlApi.runs(pipelineId, 25),
    enabled: showHistory,
  });
  // Replay a past run's per-node counters onto the canvas (read-only overlay).
  const loadRun = React.useCallback(
    (run: RunDetail) => {
      setActiveRun(run);
      const metrics = run.node_metrics ?? {};
      const failedRun = run.status !== "succeeded";
      setNodes((ns) =>
        ns.map((n) => {
          const m = metrics[n.id];
          const ok = m ? (m.errors ?? 0) === 0 : !failedRun;
          return { ...n, data: { ...n.data, metrics: m, runStatus: m || failedRun ? (ok ? "done" : "failed") : "idle" } };
        }),
      );
    },
    [setNodes],
  );

  // Deep-link `?run=<id>` (from the global "Esecuzioni · ETL" tab + the Activity
  // feed): fetch that run and pin it onto the canvas ONCE, after the pipeline has
  // hydrated (loadRun maps metrics onto existing nodes). Then strip the param so a
  // refresh / edit doesn't re-pin a stale run.
  const [searchParams, setSearchParams] = useSearchParams();
  const runParam = searchParams.get("run");
  const deepRunQ = useQuery({
    queryKey: ["etl", "run", runParam],
    queryFn: () => etlApi.getRun(runParam!),
    enabled: !!runParam,
  });
  const deepAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (deepAppliedRef.current || !runParam || !deepRunQ.data || nodes.length === 0) return;
    deepAppliedRef.current = true;
    loadRun(deepRunQ.data);
    const next = new URLSearchParams(searchParams);
    next.delete("run");
    setSearchParams(next, { replace: true });
  }, [runParam, deepRunQ.data, nodes.length, loadRun, searchParams, setSearchParams]);

  const validation = React.useMemo(
    () => validatePipeline(nodes.map((n) => ({ id: n.id, data: n.data })), edges),
    [nodes, edges],
  );

  // Fluxo "data flowing" animation: while a run is LIVE, an edge whose SOURCE
  // block has started producing (running/done) sends an emerald dot down its
  // path (the `etlFlow` custom edge reads `data.flowing`). Render-only overlay —
  // the base `edges` state stays untouched so editing/`onEdgesChange` is intact.
  const displayEdges = React.useMemo(() => {
    const live = activeRun?.status === "running";
    const byId = new Map(nodes.map((n) => [n.id, n.data]));
    return edges.map((e) => {
      const src = byId.get(e.source);
      const flowing = live && (src?.runStatus === "running" || src?.runStatus === "done");
      // Speed by throughput: items emitted on THIS edge's channel (else the
      // block's total) → faster dots the more data moved. Log scale so a trickle
      // ambles (~1.7s) and a flood rushes (~0.4s); see EtlFlowEdge for the stream.
      const ch = e.sourceHandle ?? "default";
      const out = src?.metrics?.out_by_channel?.[ch] ?? src?.metrics?.items_out ?? 0;
      const durSec = Math.min(1.7, Math.max(0.4, 1.7 - 0.28 * Math.log10(1 + out)));
      return { ...e, type: "etlFlow", data: { ...e.data, flowing, durSec } };
    });
  }, [edges, nodes, activeRun?.status]);

  if (catalogQ.isLoading || pipeQ.isLoading) {
    return <div className="flex h-full items-center justify-center text-muted-foreground">Caricamento…</div>;
  }
  if (pipeQ.isError || !pipeQ.data) {
    return <div className="flex h-full items-center justify-center text-danger">Pipeline non trovata</div>;
  }

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex items-center gap-2 border-b border-border/60 bg-surface px-3 py-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/etl")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-foreground">{pipeQ.data.name}</div>
          <div className="truncate text-[11px] text-muted-foreground">
            {nodes.length} blocchi · {edges.length} connessioni
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {canWrite && (
            <label
              className="flex cursor-pointer items-center gap-1.5 text-[11px] text-muted-foreground"
              title="Una pipeline disattivata non viene mai eseguita (né dal cron né dai workflow)"
            >
              <Switch
                checked={pipeQ.data.enabled}
                onCheckedChange={(v) => enableMut.mutate(v)}
                disabled={enableMut.isPending}
              />
              {pipeQ.data.enabled ? "Abilitata" : "Disabilitata"}
            </label>
          )}
          {validation.valid ? (
            <span className="flex items-center gap-1 text-[11px] text-emerald-500">
              <CheckCircle2 className="h-3.5 w-3.5" /> Valida
            </span>
          ) : (
            <span
              className="flex items-center gap-1 text-[11px] text-warning"
              title={validation.errors.join("\n")}
            >
              <AlertTriangle className="h-3.5 w-3.5" /> {validation.errors.length} problemi
            </span>
          )}
          {activeRun && (
            <span className="rounded bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground">
              run: {activeRun.status}
            </span>
          )}
          <Button
            variant={showHistory ? "default" : "ghost"}
            size="sm"
            onClick={() => setShowHistory((s) => !s)}
          >
            <History className="h-4 w-4" /> Esecuzioni
          </Button>
          {canWrite && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(true)}
              title="Impostazioni runtime (parallelismo, backpressure)"
            >
              <Settings2 className="h-4 w-4" />
            </Button>
          )}
          {canRun && runActive ? (
            <Button
              variant="danger"
              size="sm"
              onClick={() => cancelMut.mutate()}
              disabled={activeRun?.status === "cancelling" || cancelMut.isPending}
              title="Interrompi l'esecuzione"
            >
              {activeRun?.status === "cancelling" || cancelMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Square className="h-4 w-4 fill-current" />
              )}
              {activeRun?.status === "cancelling" ? "Interruzione…" : "Stop"}
            </Button>
          ) : canRun ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => debugMut.mutate()}
                disabled={debugMut.isPending || runMut.isPending || !validation.valid || dirty}
                title={
                  dirty
                    ? "Salva prima di eseguire"
                    : !validation.valid
                      ? "Pipeline non valida"
                      : "Esegui sui primi 100 record per ispezione (non legge tutta la sorgente)"
                }
              >
                {debugMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bug className="h-4 w-4" />}
                Debug
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => runMut.mutate()}
                disabled={runMut.isPending || debugMut.isPending || !validation.valid || dirty}
                title={dirty ? "Salva prima di eseguire" : !validation.valid ? "Pipeline non valida" : ""}
              >
                {runMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                Esegui
              </Button>
            </>
          ) : null}
          {canWrite && (
            <Button size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending || !dirty}>
              {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salva
            </Button>
          )}
        </div>
      </div>

      {/* Draft-first (Odino): an inactive NEWER version exists — the running
          pipeline is untouched until the operator activates it here. */}
      {draftVersion && canWrite && (
        <div className="flex items-center gap-2 border-b border-accent/30 bg-accent/10 px-3 py-1.5 text-xs">
          <Sparkles className="h-3.5 w-3.5 shrink-0 text-accent" />
          <span className="min-w-0 truncate">
            Bozza <b>v{draftVersion.version}</b> disponibile
            {draftVersion.notes ? ` — ${draftVersion.notes}` : ""}. La pipeline resta sulla
            versione attiva finché non la attivi.
          </span>
          <Button
            size="sm"
            variant="outline"
            className="ml-auto shrink-0"
            onClick={() => activateDraftMut.mutate(draftVersion.id)}
            disabled={activateDraftMut.isPending}
          >
            {activateDraftMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Attiva v{draftVersion.version}
          </Button>
        </div>
      )}

      {/* Body */}
      <div className="flex min-h-0 flex-1">
        {canWrite && (
          <BlockPalette
            catalog={catalogQ.data ?? {}}
            disabled={!canWrite}
            onPick={(d) => addNode(d, rf.project({ x: 400, y: 240 }))}
          />
        )}
        <div className={cn("relative min-w-0 flex-1")} onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
          <ReactFlow
            nodes={nodes}
            edges={displayEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, n) => setSelectedId(n.id)}
            onPaneClick={() => setSelectedId(null)}
            nodeTypes={NODE_TYPES}
            edgeTypes={EDGE_TYPES}
            nodesDraggable={canWrite}
            nodesConnectable={canWrite}
            elementsSelectable
            fitView
            snapToGrid
            snapGrid={[15, 15]}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable className="!bg-surface" />
          </ReactFlow>
        </div>
        {selected && selected.data.descriptor && (
          <BlockConfigPanel
            // Keyed per node: the panel's field editors seed local state once
            // (JSON textarea, row editors) — switching node must REMOUNT them
            // or the previous node's buffer bleeds onto the new one.
            key={selected.id}
            descriptor={selected.data.descriptor}
            label={selected.data.label}
            config={selected.data.config}
            metrics={selected.data.metrics}
            onChangeLabel={(v) => patchNode(selected.id, { label: v })}
            onChange={(config) => patchNode(selected.id, { config })}
            onClose={() => setSelectedId(null)}
            readOnly={!canWrite}
          />
        )}
      </div>

      {/* Execution history dock */}
      {showHistory && (
        <div className="flex h-52 shrink-0 flex-col border-t border-border/60 bg-surface">
          <div className="flex items-center justify-between border-b border-border/50 px-3 py-1.5">
            <span className="text-xs font-semibold text-muted-foreground">Esecuzioni recenti</span>
            <button
              onClick={() => setShowHistory(false)}
              className="rounded p-1 text-muted-foreground hover:bg-elevated"
              aria-label="Chiudi"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {runsQ.isLoading ? (
              <div className="p-3 text-xs text-muted-foreground">Caricamento…</div>
            ) : (runsQ.data ?? []).length === 0 ? (
              <div className="p-3 text-xs text-muted-foreground">Nessuna esecuzione.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-surface text-[10px] uppercase text-muted-foreground">
                  <tr className="border-b border-border/40">
                    <th className="px-3 py-1 text-left font-semibold">Stato</th>
                    <th className="px-3 py-1 text-left font-semibold">Trigger</th>
                    <th className="px-3 py-1 text-right font-semibold">In→Out</th>
                    <th className="px-3 py-1 text-right font-semibold">DL</th>
                    <th className="px-3 py-1 text-right font-semibold">Durata</th>
                    <th className="px-3 py-1 text-right font-semibold">Quando</th>
                  </tr>
                </thead>
                <tbody>
                  {(runsQ.data ?? []).map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => loadRun(r)}
                      className={cn(
                        "cursor-pointer border-b border-border/30 hover:bg-elevated",
                        activeRun?.id === r.id && "bg-elevated",
                      )}
                    >
                      <td className="px-3 py-1">
                        <span className={runTone(r.status)}>{r.status}</span>
                      </td>
                      <td className="px-3 py-1 text-muted-foreground">{r.trigger}</td>
                      <td className="px-3 py-1 text-right tabular-nums">
                        {r.rows_in}→{r.rows_out}
                      </td>
                      <td className={cn("px-3 py-1 text-right tabular-nums", r.dead_lettered > 0 && "text-danger")}>
                        {r.dead_lettered}
                      </td>
                      <td className="px-3 py-1 text-right tabular-nums">{fmtDuration(r)}</td>
                      <td className="px-3 py-1 text-right text-muted-foreground">
                        {r.started_at ? new Date(r.started_at).toLocaleString() : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
      {showSettings && (
        <SettingsDialog
          pipelineId={pipelineId}
          settings={settings}
          scheduleCron={pipeQ.data.schedule_cron}
          scheduleTimezone={pipeQ.data.schedule_timezone}
          scheduleOverlapPolicy={pipeQ.data.schedule_overlap_policy}
          laneId={pipeQ.data.lane_id}
          priority={pipeQ.data.priority}
          onApply={(next) => {
            setSettings(next);
            setDirty(true);
          }}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}

function runTone(status: RunStatus): string {
  if (status === "succeeded") return "text-emerald-500";
  if (status === "failed") return "text-danger";
  if (status === "cancelled") return "text-muted-foreground";
  return "text-warning"; // pending / running / cancelling
}

function fmtDuration(r: RunDetail): string {
  if (!r.started_at || !r.finished_at) return "—";
  const ms = new Date(r.finished_at).getTime() - new Date(r.started_at).getTime();
  if (ms < 0) return "—";
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function applyMetrics(
  setNodes: React.Dispatch<React.SetStateAction<Node<EtlNodeData>[]>>,
  metrics: Record<string, NodeMetricSnapshot>,
) {
  setNodes((ns) =>
    ns.map((n) => {
      const m = metrics[n.id];
      if (!m) return n;
      const active = (m.items_in ?? 0) > 0 || (m.items_out ?? 0) > 0;
      const terminal = n.data.runStatus === "done" || n.data.runStatus === "failed";
      return {
        ...n,
        data: {
          ...n.data,
          metrics: m,
          runStatus: terminal ? n.data.runStatus : active ? "running" : n.data.runStatus,
        },
      };
    }),
  );
}

export function EtlEditor({ pipelineId }: { pipelineId: string }) {
  return (
    <ReactFlowProvider>
      <EtlEditorInner pipelineId={pipelineId} />
    </ReactFlowProvider>
  );
}
